import { useState } from 'react'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import TextField from '@mui/material/TextField'

import { GeblokkeerdeWekenWarning } from '@components/GeblokkeerdeWekenWarning'
import type { DisciplineVerzameling } from '@models/DisciplineVerzameling'
import type { Entity } from '@models/Entity'
import type { GroupedFilterOption } from '@models/Filter'
import { addWeeks, formatDateToISOWeek, parseISO } from '@util/date-util'

import { SelectObject } from '../../../../core/components/SelectObject'
import type { GeblokkeerdeWeek } from '../../../../planning/types/GeblokkeerdeWeek'
import type { BijlageTemplate } from '../../../../templates/types/BijlageTemplate'
import { StyledAlert } from '../../../../theme/components/StyledAlert'
import { StyledError } from '../../../../theme/components/StyledError'
import type { Subproces } from '../../../subprocessen/types/Subproces'
import { useCreateAssignedBijlageForEntity } from '../../hooks/useCreateAssignedBijlageForEntity'

type Props = {
  assignees: GroupedFilterOption[]
  processtatus: string
  bijlageTemplates: BijlageTemplate[]
  disciplineVerzamelingen: DisciplineVerzameling[]
  entity: Entity
  projectStartUitvoeringWeek?: string
  isHoofdleidingProject?: boolean
  onClose: () => void
  geblokkeerdeWeken: GeblokkeerdeWeek[]
  subproces?: Subproces
}

const relevantTemplateFilter = (
  template: BijlageTemplate,
  processtatus: string,
  subproces?: Subproces,
) => {
  if (subproces) {
    if (template.scope !== 'subproces') {
      return false
    }

    if (!template.subprocesStatus) {
      // als niet gekoppeld aan een status kan deze altijd aangemaakt worden
      return true
    }

    // als een subproces is meegegeven zijn de templates in de component props alleen de relevant voor een subproces
    return template.subprocesStatus.includes(subproces.status)
  }

  return template.scope === 'project' && template.processtatus === processtatus
}

const buildBijlageDialogTitle = (
  processtatus: string,
  id: string,
  subproces?: Subproces,
): string | undefined => {
  if (processtatus && subproces) {
    return `Aanmaken bijlage voor project ${id} (fase ${processtatus}) en subproces ${subproces.type}`
  }

  if (processtatus) {
    return `Aanmaken bijlage voor project ${id} (fase ${processtatus})`
  }

  if (subproces) {
    return `Aanmaken bijlage voor subproces ${subproces.type}`
  }
}

// https://mijnaansluiting.atlassian.net/browse/DSP-10477
const canChangeHerplanDiff = (
  template?: BijlageTemplate,
  isHoofdleidingProject?: boolean,
) => {
  if (!isHoofdleidingProject) return false
  if (!template || !template.reference) return true
  if (template.reference === 'startUitvoeringWeek') return true

  return false
}

const findBijlageTemplate = (
  bijlageTemplates: BijlageTemplate[],
  documentsoort: any,
  processtatus: string,
  subproces?: Subproces,
) => {
  return bijlageTemplates.find(
    (bt) =>
      bt.documentsoort === documentsoort.code &&
      // zelfde filter als waarmee de documentsoorten opties zijn opgebouwd,
      // anders wordt er voor een subproces zonder processtatus niets gevonden
      relevantTemplateFilter(bt, processtatus, subproces),
  )
}

const createHerplanDiffLabel = ({
  isNaUitvoering,
  template,
}: {
  isNaUitvoering: boolean
  template?: BijlageTemplate
}) => {
  const naOfVoor = isNaUitvoering ? 'na' : 'voor'

  // zie ook references in bijlage-reference.js
  switch (template?.reference) {
    // deze twee references zijn altijd 'na': een negatieve herplanDiff is er
    // niet toegestaan, zie REFERENCES_ALLOWING_NEGATIVE_DIFF in api
    case 'dueWeek':
      return 'Aantal weken na opleveren voorganger'
    case 'creatieDatum':
      return 'Aantal weken na aanmaken bijlage'
    case 'aanvraagDatum':
      return `Aantal weken na aanvraagdatum`
    case 'startUitvoeringWeek':
    default:
      return `Aantal weken ${naOfVoor} geplande start uitvoering`
  }
}

export const Create = ({
  assignees,
  processtatus,
  bijlageTemplates,
  disciplineVerzamelingen,
  entity,
  projectStartUitvoeringWeek,
  isHoofdleidingProject,
  onClose,
  geblokkeerdeWeken,
  subproces,
}: Props) => {
  const { mutate: createAssignedBijlageForEntity, error } =
    useCreateAssignedBijlageForEntity()

  const [errors, setErrors] = useState({})
  const [documentsoort, setDocumentsoort] = useState<any>(null)
  const [title, setTitle] = useState('')
  const [discipline, setDiscipline] = useState<DisciplineVerzameling | null>(
    null,
  )
  const [assignedTo, setAssignedTo] = useState<GroupedFilterOption | null>(null)
  const [herplanDiff, setHerplanDiff] = useState(0)

  const { id, gebiedscode } = entity

  const isNaUitvoering = ['In uitvoering', 'Oplevering'].includes(processtatus)

  const template = documentsoort
    ? findBijlageTemplate(
        bijlageTemplates,
        documentsoort,
        processtatus,
        subproces,
      )
    : undefined

  const mayChangeHerplanDiff = canChangeHerplanDiff(
    template,
    isHoofdleidingProject,
  )

  const documentsoorten = bijlageTemplates
    .filter((bt) => relevantTemplateFilter(bt, processtatus, subproces))
    .map(({ documentsoort, perDiscipline, subprocesStatus, herplanDiff }) => ({
      code: documentsoort,
      label: documentsoort,
      perDiscipline,
      subprocesStatus,
      herplanDiff,
    }))

  const validate = () => {
    const documentSoortError = !documentsoort
    const assignedToError = !assignedTo
    const disciplineError = documentsoort?.perDiscipline && !discipline
    const titleError = documentsoort?.label === 'Overig' && !title

    setErrors({
      documentSoortError,
      assignedToError,
      disciplineError,
      titleError,
    })

    return !(
      documentSoortError ||
      assignedToError ||
      disciplineError ||
      titleError
    )
  }

  const onSubmitClick = () => {
    if (validate()) {
      const bijlageInfo: any = {
        //@ts-expect-error - checked in validate
        assignedTo: assignedTo.label,
        status: 'assigned',
        documentsoort: documentsoort.code,
        title,
      }

      if (processtatus) {
        bijlageInfo.processtatus = processtatus
      }

      if (discipline) {
        bijlageInfo.discipline = discipline.disciplineLabel
        bijlageInfo.netbeheerder = discipline.netbeheerderLabel
      }

      if (isHoofdleidingProject) {
        // niet meesturen als de gebruiker de waarde niet mag aanpassen: de api
        // plant een bijlage met een meegestuurde herplanDiff tov de GSU, ook als
        // de template een andere reference heeft. zie calculate-due-week-from-project
        if (herplanDiff && mayChangeHerplanDiff) {
          bijlageInfo.herplanDiff = isNaUitvoering
            ? herplanDiff // after uitvoering when in phase in uitvoering or oplevering
            : -herplanDiff // before uitvoering date when in other phases
        }

        bijlageInfo.title = title
      }

      const input = {
        entity,
        bijlageInfo,
        // het subprocesId gaat via de route mee, niet via de body. de api leidt
        // subprocesType en subprocesStatus zelf af uit het subproces en de template
        subprocesId: subproces?._id,
      }

      createAssignedBijlageForEntity(input, { onSuccess: () => onClose() })
    }
  }

  const startUitvoeringWithHerplanIsoWeek = projectStartUitvoeringWeek
    ? formatDateToISOWeek(
        addWeeks(
          parseISO(projectStartUitvoeringWeek),
          isNaUitvoering
            ? /* v8 ignore next - TODO: missing coverage -- @preserve */ herplanDiff
            : -herplanDiff,
        ),
      )
    : ''

  return (
    <>
      <DialogTitle>
        {buildBijlageDialogTitle(processtatus, id, subproces)}
      </DialogTitle>

      <DialogContent>
        <GeblokkeerdeWekenWarning
          geblokkeerdeWeken={geblokkeerdeWeken}
          isoWeek={startUitvoeringWithHerplanIsoWeek}
          gebiedscode={
            gebiedscode ||
            /* v8 ignore next - TODO: missing coverage -- @preserve */ ''
          }
        />

        {template?.waitFor && (
          <StyledAlert severity="info" sx={{ mb: 1 }}>
            Deze bijlage wacht op <strong>{template.waitFor}</strong> en krijgt
            een opleverdatum als deze allemaal zijn opgeleverd of geannuleerd.
          </StyledAlert>
        )}

        <StyledError error={error} />

        <SelectObject
          sx={{ pr: 1, pb: 1 }}
          options={documentsoorten}
          onChange={(documentsoort) => {
            setDiscipline(null)
            setDocumentsoort(documentsoort)
            // uit de optie zelf, want template is afgeleid van de state die
            // hier nog de vorige documentsoort bevat
            setHerplanDiff(Math.abs(documentsoort.herplanDiff || 0))
          }}
          isOptionEqualToValue={(option, value) => option.code === value.code}
          value={documentsoort}
          size={'medium'}
          label="Documentsoort"
          error={errors['documentSoortError']}
          disableClearable
        />

        {documentsoort?.label === 'Overig' && (
          <TextField
            sx={{ pr: 1, pb: 1 }}
            onChange={(e) => setTitle(e.target.value)}
            value={title}
            variant="standard"
            label={`Titel (${title.length}/200)`}
            fullWidth
            error={errors['titleError']}
            slotProps={{ htmlInput: { maxLength: 200 } }}
          />
        )}

        <Box sx={{ display: 'flex', pb: 1 }}>
          <SelectObject
            sx={{ width: '50%', pr: 1 }}
            options={assignees}
            groupBy={(assignedTo) => assignedTo.group}
            onChange={setAssignedTo}
            getOptionLabel={(assignedTo) => assignedTo.label}
            value={assignedTo}
            size={'medium'}
            label="Toegewezen aan"
            error={errors['assignedToError']}
          />

          <TextField
            sx={{ width: '50%', pr: 1 }}
            type="number"
            variant="standard"
            value={herplanDiff}
            label={createHerplanDiffLabel({ isNaUitvoering, template })}
            onChange={(e) => setHerplanDiff(Number(e.target.value))}
            id="opleverweek"
            disabled={!mayChangeHerplanDiff}
          />

          {documentsoort?.perDiscipline && (
            <SelectObject
              sx={{ width: '50%', pr: 1 }}
              options={disciplineVerzamelingen}
              onChange={setDiscipline}
              value={discipline}
              size="medium"
              label="Discipline"
              getOptionLabel={({ disciplineLabel, netbeheerderLabel }) =>
                `${netbeheerderLabel} - ${disciplineLabel}`
              }
              error={errors['disciplineError']}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" autoFocus>
          Annuleren
        </Button>
        <Button color="primary" onClick={onSubmitClick}>
          Opslaan
        </Button>
      </DialogActions>
    </>
  )
}
